import {
  captureExceptionWithTags,
  captureMessageWithTags,
} from 'src/utils/sentry';

import { PIPELINES_SENTRY_PRIMARY_TAG } from '../constants';

const SKIP_FLAG = '__skip__';

export enum SentryPipelinesTeam {
  Mangalyaan = 'mangalyaan',
  Pathfinder = 'pathfinder',
  Stardust = 'stardust',
  Zenith = 'zenith',
}

export const RESPONSE_UNDEFINED = 'Response undefined';
export const RESPONSE_CONTENT_UNDEFINED = 'Response content undefined';
const MAX_REGEX_MATCH_LENGTH = 99;

export const extracMessageFromJSON = (
  json: any,
  defaultMessage = RESPONSE_CONTENT_UNDEFINED
): string => {
  if (json === null || json === undefined) {
    return defaultMessage;
  }

  // our server seems returning a inconsistent data structure for errors
  // It uses {error: {message: string}},  {message: string} or {error: string} interchangably
  if (json.error?.message || json.message || json.error) {
    return json.error?.message || json.message || json.error;
  }
  if (JSON.stringify(json) !== '{}') {
    return JSON.stringify(json);
  }
  return defaultMessage;
};

/**
 * A function that will return a more specific error from the Response object.
 * Make sure it's being used before res.json() or res.text() is called,
 * otherwise it's throwing `Failed to execute 'clone' on 'Response'` error.
 * @param res - the error response
 * @returns - error message
 */
export const createErrorMessage = async (res: Response): Promise<string> => {
  if (!res) return RESPONSE_UNDEFINED;
  try {
    // try catch is needed to avoid calling .json() on an empty response, i.e. new Resposne() or new Response('')
    // which will throw an Unexpected end of JSON input error to Sentry
    let errorMessage = `${res.status}: ${res.statusText}`;
    const resClone = res.clone(); // using clone here to avoid calling `.json` or `.text` directly to cause a `Already Read` error
    const contentType = resClone.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) {
      const json = await resClone.json();
      errorMessage = extracMessageFromJSON(json);
    } else {
      const text = await resClone.text();
      if (!text || !text.length) {
        return RESPONSE_CONTENT_UNDEFINED;
      }
      errorMessage = text || errorMessage;
    }
    return errorMessage;
  } catch (error) {
    return error.toString();
  }
};

const defaultSkipMessages = [
  'Invalid or unknown installation',
  // resource not found
  'Resource not found',
  'Not found',
  // enable two-step verifications
  'To access this team, enable two-step verification.',
  'To access this user, enable two-step verification.',
  'To access this repository, enable two-step verification.',
  // whitelist ip address
  'To access this repository, an admin must whitelist your IP.',
  'To access this user, an admin must whitelist your IP.',
  'To access this team, an admin must whitelist your IP.',
  // access limits
  'You may not have access to this repository or it no longer exists in this workspace',
];

// Regex matching limited to first 100 characters of error message to prevent poor performance for large error messages
const skipRegex = [
  /Log in step .* does not exist/i,
  /504 Gateway Time-out/i,
  /502 Bad Gateway/i,
  /Bitbucket is experiencing a problem that is preventing this request from being successful/i,
  // Expected error when attempting to request from .bitbucket then fallback to root
  /(：|:)\sbitbucket-pipelines\.yml/i,
];

export const isIgnoredErrorForSentry = (
  e: Maybe<Error>,
  messagesToSkip: string[] = []
): boolean => {
  if (!e) return false;

  const message = typeof e.message === 'string' ? e.message : '';
  const name = typeof e.name === 'string' ? e.name : '';
  const skipMessages = [...defaultSkipMessages, ...messagesToSkip];

  return (
    skipMessages.includes(message) ||
    skipMessages.includes(name) ||
    skipMessages.some(m => message.startsWith(m) || name.startsWith(m)) ||
    skipRegex.some(
      regex =>
        message.substring(0, MAX_REGEX_MATCH_LENGTH).match(regex) ||
        name.substring(0, MAX_REGEX_MATCH_LENGTH).match(regex)
    )
  );
};

export const isIgnoredMessageForSentry = (
  message: string,
  messagesToSkip: string[] = []
): boolean => {
  if (!message) return false;

  const skipMessages = [...defaultSkipMessages, ...messagesToSkip];

  return (
    skipMessages.includes(message) ||
    skipMessages.some(m => message.startsWith(m)) ||
    skipRegex.some(regex =>
      message.substring(0, MAX_REGEX_MATCH_LENGTH).match(regex)
    )
  );
};

/**
 * This function wraps `captureExceptionWithTags` with a preset tag - app:pipelines.
 * @param tags - Object, optional
 * @param extras - Object, optional
 * @returns - void
 */
export const capturePipelinesExceptionWithTags = (
  e: Maybe<Error>,
  tags?: { [key: string]: string } & { teamName?: SentryPipelinesTeam },
  extras?: { [key: string]: string },
  messagesToSkip: string[] = []
): void => {
  if (isIgnoredErrorForSentry(e, messagesToSkip)) return;

  if (e?.message?.toLowerCase() === SKIP_FLAG) return;

  const mergedTags = {
    ...tags,
    app: PIPELINES_SENTRY_PRIMARY_TAG,
  };
  captureExceptionWithTags(e!, mergedTags, extras);
};

/**
 * This function wraps `captureMessageWithTags` with a preset tag - app:pipelines.
 * @param tags - Object, optional
 * @param extras - Object, optional
 * @returns - void
 */
export const capturePipelinesMessageWithTags = (
  message: string,
  tags?: { [key: string]: string } & { teamName?: SentryPipelinesTeam },
  extras?: { [key: string]: string },
  messagesToSkip: string[] = []
): void => {
  if (isIgnoredMessageForSentry(message, messagesToSkip)) return;

  if (message?.toLowerCase() === SKIP_FLAG) return;

  const mergedTags = {
    ...tags,
    app: PIPELINES_SENTRY_PRIMARY_TAG,
  };
  captureMessageWithTags(message, mergedTags, extras);
};

/**
 * Enhanced error capture function that handles error message coercion with optional custom extraction logic.
 * Always throws the entire error object to preserve error context.
 * @param error - The error object to capture
 * @param segment - The segment tag for Sentry
 * @param extractErrorDetail - Optional function to extract detailed error information
 * @param tags - Additional tags for Sentry
 * @param extras - Additional extras for Sentry
 * @param messagesToSkip - Messages to skip for Sentry reporting
 */
export const capturePipelinesError = (
  error: any,
  segment: string,
  extractErrorDetail?: (error: any) => string,
  tags?: { [key: string]: string } & { teamName?: SentryPipelinesTeam },
  extras?: { [key: string]: string },
  messagesToSkip: string[] = []
): void => {
  let errorDetail = '';

  if (extractErrorDetail) {
    try {
      errorDetail = extractErrorDetail(error);
    } catch {
      // If custom extraction fails, fall back to default
    }
  }

  const errorMessage = errorDetail || extracMessageFromJSON(error);

  capturePipelinesMessageWithTags(
    errorMessage,
    {
      segment,
      ...tags,
    },
    {
      detailedMessage: errorDetail,
      ...extras,
    },
    messagesToSkip
  );
};
